<script setup>
import { computed, ref } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
// Zimobi: the cards ("chamados") panel is a second tab of this sidebar.
import ConversationCardsPanel from 'custom/dashboard/routes/dashboard/conversation/ConversationCardsPanel.vue';
import { useConversationSidebarTab } from 'custom/dashboard/composables/zimobi/useConversationSidebarTab';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const { isCardsTabOpen } = useConversationSidebarTab();
// Zimobi: the card modal and its dialogs teleport outside this sidebar.
const isCardModalOpen = ref(false);

const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;

  if (isContactSidebarOpen) {
    // Zimobi: tab 1 is the cards panel.
    return isCardsTabOpen.value ? 1 : 0;
  }
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  // Zimobi: interacting with a teleported card dialog must keep its parent mounted.
  if (isCardModalOpen.value) return;
  if (isSmallScreen.value && uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};
</script>

<template>
  <div
    v-on-click-outside="() => closeContactPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm max-sm:max-w-none transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        // Zimobi: either tab keeps the sidebar visible.
        'md:flex': activeTab !== null,
        'md:hidden': activeTab === null,
      },
    ]"
  >
    <!-- Zimobi: max-sm:max-w-none keeps the contact panel full screen on phones, as before the upstream drawer. -->
    <div class="flex flex-1 overflow-auto">
      <ContactPanel
        v-show="activeTab === 0"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
      <!-- Zimobi: the conversation's cards, opened from the sidebar switch. -->
      <ConversationCardsPanel
        v-if="activeTab === 1"
        :conversation-id="currentChat.id"
        :contact-name="currentChat.meta?.sender?.name"
        @modal-toggle="isCardModalOpen = $event"
      />
    </div>
  </div>
</template>
